import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import OutsideClickHandler from 'react-outside-click-handler/esm/OutsideClickHandler';
import cn from 'classnames';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { withTranslation } from 'react-i18next';

import PlusIcon from 'components/common/icons/redesign/PlusIcon';
import SimpleButton from 'components/common/Form/SimpleButton';
import CustomTooltip from 'components/common/CustomTooltip';

import style from './LanguageDropDown.module.scss';

class LanguageDropDown extends Component {
    static propTypes = {
        label: PropTypes.string,
        languages: PropTypes.array,
        onSelect: PropTypes.func,
    };

    static defaultProps = {
        label: 'Languages',
        languages: [],
    };

    constructor(props) {
        super(props);

        this.state = {
            isOpen: false,
            searchInputValue: '',
            tooltipHovered: false,
        };

        this.searchInputRef = createRef();
    }

    componentDidMount() {
        this.initRefs();
    }

    toggleList = () => {
        this.setState((state) => ({ isOpen: !state.isOpen, searchInputValue: '' }), this.focusInputRef);
    };

    focusInputRef = () => this.searchInputRef.current && this.searchInputRef.current.focus();

    handleLanguageSelect = (language) => {
        this.props.onSelect(language);
        this.toggleList();
    };

    initRefs = () => {
        const optionsRefs = {};

        this.props.languages.forEach((language) => (optionsRefs[language.label] = createRef()));

        this.setState({ optionsRefs });
    };

    handleSearchInput = (event) => this.setState({ searchInputValue: event.target.value });

    renderPreview = () => {
        if (this.state.isOpen)
            return (
                <input
                    ref={this.searchInputRef}
                    onChange={this.handleSearchInput}
                    value={this.state.searchInputValue}
                    className={style.searchInput}
                />
            );

        return this.props.label;
    };

    renderOptions = (options) => {
        const { t } = this.props;
        if (!options.length) return <div className={style.option}>{t('common.noMatches')}</div>;

        return options.map((language) => (
            <div key={language.value} className={style.option} onClick={() => this.handleLanguageSelect(language)}>
                {language.label}
            </div>
        ));
    };

    handleClickOutside() {
        this.closeList();
    }

    closeList() {
        this.setState({ isOpen: false });
    }

    render() {
        const { languages, customTooltipText } = this.props;
        const { isOpen } = this.state;

        const dropDownClassName = cn(style.dropDown, { [style.opened]: isOpen });

        // eslint-disable-next-line no-useless-escape
        const regex = new RegExp(`\^${this.state.searchInputValue}`, 'ig');
        const filteredLanguages = languages.filter((language) => {
            return regex.test(language.label);
        });

        return (
            <OutsideClickHandler onOutsideClick={() => this.state.isOpen && this.toggleList()}>
                <div className={style.container}>
                    <SimpleButton
                        className={style.selectButton}
                        onClick={this.toggleList}
                        onMouseEnter={() => this.setState({ tooltipHovered: true })}
                        onMouseLeave={() => this.setState({ tooltipHovered: false })}
                    >
                        {this.renderPreview()}
                        <PlusIcon className={style.plusIcon} />
                    </SimpleButton>
                    {customTooltipText && (
                        <div className={style.customTooltipContainer}>
                            <CustomTooltip
                                text={customTooltipText}
                                position='bottom'
                                displayed={this.state.tooltipHovered && !this.state.isOpen}
                            />
                        </div>
                    )}
                    <div className={dropDownClassName} onClick={(e) => e.stopPropagation()}>
                        <Scrollbars autoHeight autoHeightMax='10rem'>
                            {this.renderOptions(filteredLanguages)}
                        </Scrollbars>
                    </div>
                </div>
            </OutsideClickHandler>
        );
    }
}

export default withTranslation()(LanguageDropDown);
